import { Combobox, type ComboboxOption } from '@/components/cargofl-ui';
import { useCreateCategory, type AssetCategory, type AssetSummary } from '@/lib/assets-api';
import { useCan } from '@/lib/session';

/**
 * The searchable pickers, wrapped once so every screen shapes its options the
 * same way.
 *
 * A `Select` was fine when this app had no data in it. With 76 employees and a real
 * register it is not: finding "Amit More" meant scrolling a list that shows eight rows
 * at a time. The Combobox types-to-filter and never renders the whole list into the
 * page.
 *
 * `onCreate` is offered on Category and Department and deliberately NOT on Employee or
 * Asset. A department or a type is just a name, so typing it IS the whole record - but
 * an employee created from a dropdown would have no employee code, and an asset
 * created from one would have no code, serial or price. Both belong on their own
 * screens where those fields exist. That is a judgement about the record, not about
 * the widget.
 */

interface PickerEmployee {
  id: string;
  employeeCode: string;
  name: string;
  department?: string | null;
  status?: string;
  assetsHeld?: number;
}

export function EmployeePicker({
  employees,
  value,
  onChange,
  /**
   * Adds a row for "nobody in particular", selected when `value` is empty.
   *
   * The asset-request form uses it for "me": leaving the field alone is the common case
   * and must not require finding your own name in a list of ninety. Assign and Return do
   * not pass it - there, an unpicked employee is an incomplete form, not a default.
   */
  allLabel,
  placeholder = 'Search by name, employee ID or department',
  disabled,
}: {
  employees: PickerEmployee[] | undefined;
  value: string;
  onChange: (id: string) => void;
  allLabel?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const options: ComboboxOption[] = [
    ...(allLabel ? [{ value: '', label: allLabel }] : []),
    ...(employees ?? []).map((employee) => ({
      value: employee.id,
      // The NAME is the label, because that is what someone is thinking of when they
      // come to this field. The code and department go underneath, where they are still
      // searchable - the combobox matches on the description too, so typing "EMP-1012"
      // or "Implementation" both work.
      label: employee.name,
      description: [employee.employeeCode, employee.department].filter(Boolean).join(' · '),
      hint:
        employee.assetsHeld && employee.assetsHeld > 0 ? `${employee.assetsHeld} held` : undefined,
    })),
  ];

  return (
    <Combobox
      // With an allLabel there IS a row for the empty value, so '' must stay '' to select
      // it. Without one, '' means nothing is chosen and null is what shows the placeholder.
      value={allLabel ? value : value || null}
      onChange={onChange}
      options={options}
      placeholder={allLabel ?? 'Choose an employee'}
      searchPlaceholder={placeholder}
      emptyText="No employee matches"
      disabled={disabled}
      aria-label="Employee"
    />
  );
}

export function AssetPicker({
  assets,
  value,
  onChange,
  /** Shown under each row instead of the serial - used on the Return screen, where
   *  who has it is the thing you are actually looking for. */
  showHolder = false,
  placeholder = 'Search by code, name or serial',
  disabled,
}: {
  assets: AssetSummary[] | undefined;
  value: string;
  onChange: (id: string) => void;
  showHolder?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  const options: ComboboxOption[] = (assets ?? []).map((asset) => ({
    value: asset.id,
    label: asset.name,
    description: [
      asset.code,
      showHolder ? asset.holder?.name : asset.serial,
      showHolder ? undefined : asset.categoryName,
    ]
      .filter(Boolean)
      .join(' · '),
  }));

  return (
    <Combobox
      value={value || null}
      onChange={onChange}
      options={options}
      placeholder="Choose an asset"
      searchPlaceholder={placeholder}
      emptyText="No asset matches"
      disabled={disabled}
      aria-label="Asset"
    />
  );
}

/**
 * Asset type.
 *
 * The list is 12 types today and grows every time an import meets a word it does not
 * recognise, so it is exactly the "comes from the database and can grow" case the
 * Combobox exists for - a Select made you scroll past Docking Station to reach
 * Keyboard.
 *
 * `allowCreate` is on only where a type can legitimately be born: registering an
 * asset. Creating one from a FILTER would mint an empty category as a side effect of
 * looking for something, which is how junk types appear in a dropdown nobody
 * maintains.
 */
export function CategoryPicker({
  categories,
  value,
  onChange,
  /** Adds an "Any type" row. Filters pass this; the register form does not. */
  allLabel,
  allowCreate = false,
  showCounts = false,
  className,
  disabled,
}: {
  categories: AssetCategory[] | undefined;
  value: string;
  onChange: (id: string) => void;
  allLabel?: string;
  allowCreate?: boolean;
  showCounts?: boolean;
  className?: string;
  disabled?: boolean;
}) {
  const canManage = useCan('manageAssets');
  const create = useCreateCategory();

  const options: ComboboxOption[] = [
    ...(allLabel ? [{ value: '', label: allLabel }] : []),
    ...(categories ?? []).map((category) => ({
      value: category.id,
      label: category.name,
      hint: showCounts && category._count ? String(category._count.assets) : undefined,
    })),
  ];

  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={options}
      placeholder={allLabel ?? 'Choose a type'}
      searchPlaceholder="Search asset types"
      emptyText="No type matches"
      className={className}
      disabled={disabled}
      aria-label="Asset type"
      // Awaited by the Combobox, so the popup stays open until the type exists and
      // then closes with it already selected.
      onCreate={
        allowCreate
          ? async (name) => {
              const category = await create.mutateAsync({ name });
              onChange(category.id);
            }
          : undefined
      }
      createLabel="Add type"
      canCreate={canManage}
    />
  );
}

/**
 * Department.
 *
 * A plain string on the employee, not a foreign key, so "creating" one is just
 * accepting what was typed - there is no endpoint behind it.
 *
 * That is precisely why this field needs the combobox more than the others. It was a
 * free-text Input, which meant "Implementation", "implementation" and "Implementaton"
 * were three departments and the filter above could only ever find one of them. The
 * Combobox shows what already exists first and suppresses the create row on a
 * case-insensitive match, so the existing spelling wins by default.
 */
/**
 * Keeps a free-text value visible on the trigger.
 *
 * Combobox labels the trigger by finding the value among its options, so a name the
 * user just created - which is not in the list yet, and will not be until the asset is
 * saved - showed the placeholder instead, as if nothing had been chosen. Only for
 * pickers whose value IS its own label; an employee id has no business being rendered
 * raw, which is why Combobox cannot do this for everyone.
 */
function withOwnValue(options: ComboboxOption[], value: string): ComboboxOption[] {
  if (!value || options.some((option) => option.value === value)) return options;
  return [{ value, label: value }, ...options];
}

/**
 * A plain string field that suggests what has been typed into it before - brand and
 * asset name on the register form.
 *
 * Same shape as DepartmentPicker and for the same reason. As free-text Inputs these
 * produced "Lenovo", "lenovo" and "LENEVO" as three brands, and a dozen spellings of
 * one laptop model, which no filter and no report could ever bring back together. The
 * options are what is already registered under the type being chosen, so the list is
 * short and relevant, and the create row is suppressed on a case-insensitive match -
 * the existing spelling wins unless somebody deliberately types a new one.
 *
 * Creating is always allowed. The first Asus in the company has to be typeable, and a
 * picker that could not accept a new value would send people back to guessing.
 */
export function SuggestionPicker({
  suggestions,
  value,
  onChange,
  noun,
  emptyText,
  className,
  disabled,
}: {
  suggestions: string[] | undefined;
  value: string;
  onChange: (value: string) => void;
  /** Used in the placeholder and the empty row: "brand", "asset name". */
  noun: string;
  /**
   * Overrides the empty row. The default says "under this type", which is true of
   * brand and model - they are scanned within the chosen category - but wrong for a
   * location, which is scanned across the whole register.
   */
  emptyText?: string;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={withOwnValue(
        (suggestions ?? []).map((option) => ({ value: option, label: option })),
        value,
      )}
      placeholder={`Choose or type a ${noun}`}
      searchPlaceholder={`Search ${noun}s`}
      emptyText={emptyText ?? `Nothing registered under this type yet - type to add the first`}
      className={className}
      disabled={disabled}
      aria-label={noun}
      onCreate={(typed) => onChange(typed)}
      createLabel="Use"
    />
  );
}

export function DepartmentPicker({
  departments,
  value,
  onChange,
  allLabel,
  allowCreate = false,
  className,
  disabled,
}: {
  departments: string[] | undefined;
  value: string;
  onChange: (department: string) => void;
  allLabel?: string;
  allowCreate?: boolean;
  className?: string;
  disabled?: boolean;
}) {
  const options: ComboboxOption[] = withOwnValue(
    [
      ...(allLabel ? [{ value: '', label: allLabel }] : []),
      ...(departments ?? []).map((department) => ({ value: department, label: department })),
    ],
    value,
  );

  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={options}
      placeholder={allLabel ?? 'Choose a department'}
      searchPlaceholder="Search departments"
      emptyText="No department matches"
      className={className}
      disabled={disabled}
      aria-label="Department"
      onCreate={allowCreate ? (name) => onChange(name) : undefined}
      createLabel="Use"
    />
  );
}
